import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { uploadChunked } from "../lib/upload";
import { authStore } from "../lib/api";
import { useApp } from "../context/AppContext";
import { TEAMS } from "../data/constants";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "./ui/sonner";
import Avatar from "./Avatar";
import { MessageSquarePlus, Paperclip, Link2, AtSign, Send, Trash2, FileText, Film, X, Loader2, ExternalLink, Image as ImageIcon, Check, Download, Share2, Mail, Copy, AlertTriangle, RefreshCw } from "lucide-react";
import { fmtD, fmtDT } from "../lib/date";
import { OwnerLinkCheck } from "./OwnerLinkCheck";
import { MediaLightbox } from "./MediaLightbox";
import { ProjectKeywordInput, useProjectKeywordSearch } from "./ProjectKeywordSearch";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CHUNK = 5 * 1024 * 1024; // 5MB
const MAX_BYTES = 200 * 1024 * 1024; // 200MB

/* 「按阶段」分组的旧阶段名归并（2026-10-24 老板确认方案 A：只动显示层，不改历史数据）。
 *
 * 背景：日志的 phaseAtPost 记录的是发帖那一刻系统里的阶段名，而项目历史上并存过
 * 两套阶段体系——
 *   · 旧 7 阶段："1-Pre-construction"/"2-Foundation"/…（英文带序号，老施工板与
 *     开工流转写入，projects.phase 至今仍是这套）；
 *   · 新 8 阶段："开工前准备"/"拆除"/…（任务卡引擎 engine_module.PHASE_NAMES，
 *     现场快照下拉与近期日志在用）。
 * 结果照片墙按阶段分组时，同一施工阶段出现两个名字（1-Pre-construction ≠ 开工前
 * 准备），老板反馈「下拉和施工日志对不上」。
 *
 * 做法：展示前把旧叫法映射到新阶段名再分组/打标签，phaseAtPost 原始字段一律不写回。
 * 映射关系对齐后端 engine_module.LEGACY_TO_ENGINE = {0:0,1:2,3:…}（旧 index → 引擎
 * index）；"N-xxx" 序号从 1 起（"1-Pre-construction" = 旧 index 0）。中文旧叫法
 * （phase_standards.PHASE_LABELS）一并兜底。"design"（设计期发的日志）与未知值原样
 * 展示。后端阶段体系若再调整，此处需同步。
 */
const ENGINE_PHASE_NAMES = ["开工前准备", "拆除", "基础", "框架结构", "粗装 MEP", "精装前", "精装收尾", "市政收尾验收"];
const LEGACY_NUM_TO_ENGINE = { 0: 0, 1: 2, 2: 3, 3: 4, 4: 5, 5: 6, 6: 7, 7: 8 };
const LEGACY_ZH_TO_NUM = { "前期准备": 0, "地基": 2, "框架": 3, "粗装机电": 4, "精装": 6, "市政验收": 7 };
const displayPhase = (raw, zh = true) => {
  const s = String(raw ?? "").trim();
  if (!s) return raw;
  if (s in LEGACY_ZH_TO_NUM) return ENGINE_PHASE_NAMES[LEGACY_ZH_TO_NUM[s]];
  const m = s.match(/^(\d+)\s*[-–—]\s*/); // "1-Pre-construction" 这类带序号的旧叫法（中英文都兜住）
  if (m) {
    const eng = LEGACY_NUM_TO_ENGINE[Number(m[1]) - 1];
    // 引擎第 8 格（index 8）= 已竣工：旧体系收尾态没有独立日志叫法，按界面语言给文案
    if (eng != null) return eng >= ENGINE_PHASE_NAMES.length ? (zh ? "已竣工" : "Completed") : ENGINE_PHASE_NAMES[eng];
  }
  return raw;
};

/**
 * 把一条日志的 links 统一成「可直接渲染的字符串」。
 *
 * 需求背景 / Bug 现象（2026-10-22）：生产项目点开「施工日志」整个系统白屏。
 * 根因是 links 存在两种历史形态：
 *   · 前端发帖写入的是字符串数组：["https://…"]；
 *   · 后端自动生成的日志（Muse 日报批准后 chat_hub 写库）是对象数组：[{url, title}]。
 * 旧代码直接 `{l}` 渲染，遇到对象即抛
 * 「Objects are not valid as a React child」，且全站没有错误边界 → 整页白屏。
 *
 * 取舍：只在展示层做兼容归一，不回写历史数据（存量日志可能很多，回写风险大于收益）；
 * 同时后端新写入统一为字符串，避免继续产生对象形态。
 */
const normalizeLinks = (links) => (Array.isArray(links) ? links : [])
  .map((l) => {
    if (typeof l === "string") return { url: l, title: "" };
    if (l && typeof l === "object") return { url: String(l.url || ""), title: String(l.title || "") };
    return null;
  })
  .filter((l) => l && l.url);

export default function ProjectDailyLogs({ projectId, focusNoteId, onChanged }) {
  const { lang, user, api, directory, avatarFor } = useApp();
  const zh = lang === "zh";
  const [notes, setNotes] = useState([]);
  // 加载态与失败态：区分「正在加载」「加载失败」「确实没有日志」三种情况。
  // 需求背景（2026-10-22）：原来 load() 用 `catch { setNotes([]) }` 把请求失败
  // 伪装成空数组，页面显示「暂无记录，发布第一条项目日志」，用户会以为日志被删光了；
  // 首屏在响应回来前也会先闪一下同样的空态。现改为三态可分辨，失败时给重试入口。
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [filter, setFilter] = useState("all"); // all | media | mine
  const [keyword, setKeyword] = useState("");
  const [wall, setWall] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const [text, setText] = useState("");
  const [links, setLinks] = useState([]);
  const [linkInput, setLinkInput] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [important, setImportant] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [uploading, setUploading] = useState(null); // {name, pct}
  const [posting, setPosting] = useState(false);
  const [wallGroup, setWallGroup] = useState("stage"); // stage | date
  // 施工日志图片墙的当前预览索引；照片墙按日期/阶段可见顺序浏览，不跳出应用开新标签。
  const [wallPreview, setWallPreview] = useState(null);
  const [atQuery, setAtQuery] = useState(null);
  const [atStart, setAtStart] = useState(0);
  const [showExport, setShowExport] = useState(false);
  const [exp, setExp] = useState({ start: "", end: "", imagesOnly: false, to: "" });
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [shareDays, setShareDays] = useState("30");
  const [busy, setBusy] = useState(false);
  const search = useProjectKeywordSearch({ api, projectId, kind: "notes", keyword, filter });
  const fileRef = useRef(null);
  const taRef = useRef(null);
  const token = authStore.get("ali_token");
  const fileUrl = (fid) => `${BACKEND}/api/files/${fid}/download?auth=${token}`;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get(`/projects/${projectId}/notes`);
      const arr = data || [];
      const needRead = arr.some((n) => (n.mentions || []).includes(user.username) && !(n.readBy || []).includes(user.username));
      if (needRead) {
        try { await api.post(`/projects/${projectId}/notes/mark-read`); } catch {}
        setNotes(arr.map((n) => ((n.mentions || []).includes(user.username) ? { ...n, readBy: [...(n.readBy || []), user.username] } : n)));
      } else setNotes(arr);
      onChanged?.(arr.length);
    } catch (e) {
      // 保留上一次成功加载的数据，避免一次抖动就把已有日志清空；
      // 仅在没有历史数据时才呈现为错误态（由下方渲染区分），不再伪装成「暂无记录」。
      setLoadError(e?.response?.data?.detail || e?.message || "load failed");
    } finally {
      setLoading(false);
    }
  }, [api, projectId, user.username]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!focusNoteId || notes.length === 0) return;
    setHighlight(focusNoteId);
    const el = document.querySelector(`[data-testid='note-item-${focusNoteId}']`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setHighlight(null), 3500);
    return () => clearTimeout(t);
  }, [focusNoteId, notes]);

  const nameOf = (u) => (zh ? (u.nameZh || u.name) : (u.name || u.nameZh)) || u.username;
  const users = (directory || []).filter((u) => u.username !== user.username);

  const isMedia = (a) => a.kind === "image" || a.kind === "video";
  // 搜索时以服务端返回的历史匹配结果为数据源；清空后恢复原列表及图片墙行为。
  const filteredNotes = (search.active ? search.items : notes).filter((n) => {
    if (filter === "media") return (n.attachments || []).some(isMedia);
    if (filter === "mine") return (n.mentions || []).includes(user.username);
    return true;
  });
  const mediaItems = (search.active ? filteredNotes : notes).flatMap((n) => (n.attachments || []).filter(isMedia).map((a) => ({ ...a, note: n })));

  const groups = useMemo(() => {
    const g = [];
    const teamIds = [...new Set(users.filter((u) => u.team).map((u) => u.team))];
    teamIds.forEach((tid) => {
      const tm = TEAMS.find((t) => t.id === tid);
      const members = users.filter((u) => u.team === tid).map((u) => u.username);
      if (members.length) g.push({ key: `team-${tid}`, label: (zh ? "施工组·" : "Team·") + (tm ? (zh ? tm.zh : tm.name) : tid), members });
    });
    [["finance", zh ? "财务部" : "Finance"], ["sales", zh ? "销售部" : "Sales"], ["pm", "PM"], ["eng_manager", zh ? "工程部" : "Construction Dept"]].forEach(([role, lbl]) => {
      const members = users.filter((u) => u.role === role).map((u) => u.username);
      if (members.length) g.push({ key: `role-${role}`, label: lbl, members });
    });
    return g;
  }, [users, zh]);
  const addGroup = (members) => setMentions((prev) => [...new Set([...prev, ...members])]);

  const exportPdf = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/notes/export-pdf`, { params: { lang: zh ? "zh" : "en", start: exp.start || undefined, end: exp.end || undefined, imagesOnly: exp.imagesOnly }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "project_log.pdf"; a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const emailOwner = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/notes/export-email`, { lang: zh ? "zh" : "en", start: exp.start || undefined, end: exp.end || undefined, imagesOnly: exp.imagesOnly, to: exp.to || undefined });
      if (data.ok) toast.success((zh ? "已邮寄业主 " : "Emailed ") + data.to);
      else toast.message((zh ? "报告已生成，但邮件未送达（优雅降级）" : "Generated; email not delivered") + " · " + data.to);
      setShowExport(false);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const openShare = async () => {
    setShareOpen(true);
    try { const { data } = await api.get(`/projects/${projectId}/share-link`); setShareLink(data); } catch { setShareLink(null); }
  };
  const genShare = async (rotate) => {
    try {
      const path = rotate ? `/projects/${projectId}/share-link/rotate` : `/projects/${projectId}/share-link`;
      const { data } = await api.post(path, { days: shareDays === "0" ? null : Number(shareDays) });
      setShareLink(data);
      try { await navigator.clipboard.writeText(`${window.location.origin}/share/${data.token}`); toast.success(zh ? "链接已复制" : "Link copied"); } catch {}
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const revokeShare = async () => {
    try { await api.post(`/projects/${projectId}/share-link/revoke`); setShareLink(null); toast.success(zh ? "链接已失效" : "Revoked"); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const shareUrl = shareLink && shareLink.token ? `${window.location.origin}/share/${shareLink.token}` : "";

  const jumpTo = (id) => {
    // 历史跳转可能命中当前关键词之外的日志，先清除筛选再定位。
    setKeyword(""); setFilter("all"); setWall(false);
    setHighlight(id);
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid='note-item-${id}']`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    setTimeout(() => setHighlight(null), 3000);
  };

  const wallGroups = useMemo(() => {
    const map = new Map();
    mediaItems.forEach((a) => {
      // 分组键用归并后的阶段名：旧叫法与新叫法落到同一组（见顶部 displayPhase 说明）
      const key = wallGroup === "date" ? String(a.note.createdAt).slice(0, 10) : (displayPhase(a.note.phaseAtPost, zh) || (zh ? "未标注阶段" : "Unlabeled"));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    });
    return [...map.entries()];
  }, [mediaItems, wallGroup, zh]);

  // 反馈 FBa9009f4f9a：施工日志照片墙原来用普通链接打开单张原图，失去前后照片队列，
  // 因此手机上无法左右滑动连续查看。把当前照片墙的分组顺序展开成同一个队列，
  // 并复用项目通用灯箱已有的触屏滑动、方向键和前后按钮；这只影响照片墙，不改变日志正文附件链接。
  const wallItems = wallGroups.flatMap(([, items]) => items);
  const wallLightboxItems = wallItems.map((a) => {
    const phase = displayPhase(a.note.phaseAtPost, zh) || (zh ? "未标注阶段" : "Unlabeled");
    const date = String(a.note.createdAt || "").slice(5, 10);
    return {
      id: a.fileId,
      fileId: a.fileId,
      url: fileUrl(a.fileId),
      kind: a.kind === "image" ? "photo" : "video",
      name: a.filename || a.fileId,
      meta: [phase, date].filter(Boolean).join(" · "),
    };
  });

  const onTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    const caret = e.target.selectionStart ?? val.length;
    const upto = val.slice(0, caret);
    const at = upto.lastIndexOf("@");
    if (at >= 0 && !/\s/.test(upto.slice(at + 1))) { setAtStart(at); setAtQuery(upto.slice(at + 1)); }
    else setAtQuery(null);
  };
  const applyMention = (label, usernames) => {
    const caret = taRef.current?.selectionStart ?? text.length;
    const newText = `${text.slice(0, atStart)}@${label} ${text.slice(caret)}`;
    setText(newText);
    setMentions((prev) => [...new Set([...prev, ...usernames])]);
    setAtQuery(null);
    setTimeout(() => taRef.current?.focus(), 0);
  };
  const atMatches = useMemo(() => {
    if (atQuery === null) return { groups: [], users: [] };
    const q = atQuery.toLowerCase();
    return {
      groups: groups.filter((g) => g.label.toLowerCase().includes(q)).slice(0, 4),
      users: users.filter((u) => (nameOf(u) || "").toLowerCase().includes(q) || u.username.toLowerCase().includes(q)).slice(0, 6),
    };
  }, [atQuery, groups, users, zh]); // eslint-disable-line

  const addLink = () => {
    let v = linkInput.trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v)) v = "https://" + v;
    setLinks([...links, v]); setLinkInput("");
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const file of files) {
      if (file.size > MAX_BYTES) { toast.error(zh ? `文件过大（上限 200MB）：${file.name}` : `File too large (max 200MB): ${file.name}`); continue; }
      try {
        setUploading({ name: file.name, pct: 0 });
        const data = await uploadChunked(api, file, { onPct: (pct) => setUploading({ name: file.name, pct }) });
        setAttachments((a) => [...a, data]);
      } catch (err) { toast.error((zh ? "上传失败：" : "Upload failed: ") + (err?.response?.data?.detail || err.message)); }
      finally { setUploading(null); }
    }
  };

  const submit = async () => {
    if (!text.trim() && attachments.length === 0 && links.length === 0) { toast.error(zh ? "请填写内容或添加附件" : "Add text or attachment"); return; }
    setPosting(true);
    try {
      await api.post(`/projects/${projectId}/notes`, { text: text.trim(), links, attachments, mentions, important });
      toast.success(important ? (zh ? "已发布重要更新，已提醒负责销售" : "Important update posted") : (zh ? "已发布" : "Posted"));
      setText(""); setLinks([]); setAttachments([]); setMentions([]); setShowMentions(false); setImportant(false);
      await load();
      search.refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setPosting(false); }
  };

  const del = async (n) => {
    if (!window.confirm(zh ? "删除该记录？" : "Delete this note?")) return;
    try { await api.delete(`/projects/${projectId}/notes/${n.id}`); await load(); search.refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="border-t border-slate-100 pt-4" data-testid="daily-logs-section">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
          <MessageSquarePlus className="h-4 w-4 text-brand-500" />{zh ? "项目日志 · 部门协同" : "Daily Logs · Collaboration"}
          <span className="text-xs font-normal text-slate-400">· {notes.length}</span>
        </p>
        <div className="ml-auto flex items-center gap-2">
          {notes.length > 0 && (
            <select data-testid="log-history-select" defaultValue="" onChange={(e) => { if (e.target.value) { jumpTo(e.target.value); e.target.value = ""; } }}
              className="text-xs rounded-md border border-slate-200 px-2 py-1.5 max-w-[220px] bg-white focus:outline-none focus:ring-1 focus:ring-brand-400">
              <option value="">{zh ? "历史记录…" : "History…"}</option>
              {notes.map((n) => {
                const label = `${String(n.createdAt).slice(5, 16).replace("T", " ")} · ${(zh ? (n.authorNameZh || n.authorName) : n.authorName)} · ${(n.text || (zh ? "（附件/链接）" : "(attachment)")).slice(0, 14)}`;
                return <option key={n.id} value={n.id}>{label}</option>;
              })}
            </select>
          )}
          {notes.length > 0 && (
            <Button data-testid="log-export-pdf-btn" variant="outline" size="sm" onClick={() => setShowExport(true)} className="text-xs"><Download className="h-3.5 w-3.5 mr-1" />{zh ? "导出PDF" : "Export PDF"}</Button>
          )}
          <Button data-testid="log-share-btn" variant="outline" size="sm" onClick={openShare} className="text-xs"><Share2 className="h-3.5 w-3.5 mr-1" />{zh ? "分享业主" : "Share"}</Button>
        </div>
      </div>

      {/* Composer */}
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2.5">
        <div className="relative">
          <textarea data-testid="note-text-input" ref={taRef} value={text} onChange={onTextChange} onBlur={() => setTimeout(() => setAtQuery(null), 150)} rows={2}
            placeholder={zh ? "写下进度、问题或留言…输入 @ 可快捷提醒同事，可添加图片/PDF/视频/链接" : "Write an update… type @ to mention, attach image/PDF/video/link"}
            className="w-full text-sm rounded-md border border-slate-200 px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-brand-400 resize-none" />
          {atQuery !== null && (atMatches.groups.length > 0 || atMatches.users.length > 0) && (
            <div className="absolute z-40 left-0 right-0 top-full mt-1 max-h-52 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg p-1" data-testid="at-suggest">
              {atMatches.groups.map((g) => (
                <button key={g.key} type="button" data-testid={`at-suggest-group-${g.key}`} onMouseDown={(e) => { e.preventDefault(); applyMention(g.label, g.members); }}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left hover:bg-red-50">
                  <AtSign className="h-3.5 w-3.5 text-red-500" /><span className="flex-1 truncate">{g.label}</span><span className="text-[10px] text-slate-400">{g.members.length}</span>
                </button>
              ))}
              {atMatches.users.map((u) => (
                <button key={u.username} type="button" data-testid={`at-suggest-${u.username}`} onMouseDown={(e) => { e.preventDefault(); applyMention(nameOf(u), [u.username]); }}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left hover:bg-slate-50">
                  <Avatar src={u.avatar} name={nameOf(u)} size={20} /><span className="flex-1 truncate">{nameOf(u)}</span><span className="text-[10px] text-slate-400 capitalize">{u.role}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* staged attachments */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {attachments.map((a, i) => (
              <span key={a.fileId} className="flex items-center gap-1.5 text-xs bg-white border border-slate-200 rounded-full pl-2 pr-1 py-1">
                <AttachIcon kind={a.kind} /><span className="max-w-[140px] truncate">{a.filename}</span>
                <button onClick={() => setAttachments(attachments.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        )}
        {/* staged links */}
        {links.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {links.map((l, i) => (
              <span key={i} className="flex items-center gap-1.5 text-xs bg-white border border-slate-200 rounded-full pl-2 pr-1 py-1">
                <Link2 className="h-3 w-3 text-sky-600" /><span className="max-w-[160px] truncate">{l}</span>
                <button onClick={() => setLinks(links.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        )}
        {/* mention chips */}
        {mentions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {mentions.map((m) => {
              const u = (directory || []).find((x) => x.username === m);
              return <span key={m} className="flex items-center gap-1 text-xs bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5"><AtSign className="h-3 w-3" />{u ? nameOf(u) : m}</span>;
            })}
          </div>
        )}

        {uploading && (
          <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="note-upload-progress">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /><span className="truncate max-w-[160px]">{uploading.name}</span>
            <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-red-500" style={{ width: `${uploading.pct}%` }} /></div>
            <span>{uploading.pct}%</span>
          </div>
        )}

        {/* toolbar */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <input ref={fileRef} type="file" multiple accept="image/*,video/*,application/pdf" onChange={onPickFiles} className="hidden" data-testid="note-file-input" />
          <Button type="button" data-testid="note-attach-btn" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="text-xs"><Paperclip className="h-3.5 w-3.5 mr-1" />{zh ? "附件" : "Attach"}</Button>
          <div className="flex items-center gap-1">
            <input data-testid="note-link-input" value={linkInput} onChange={(e) => setLinkInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLink())}
              placeholder={zh ? "粘贴网址…" : "Paste URL…"} className="text-xs rounded-md border border-slate-200 px-2 py-1.5 w-32 focus:outline-none focus:ring-1 focus:ring-brand-400" />
            <Button type="button" data-testid="note-add-link-btn" variant="outline" size="sm" onClick={addLink} className="text-xs"><Link2 className="h-3.5 w-3.5" /></Button>
          </div>
          <div className="relative">
            <Button type="button" data-testid="note-mention-toggle" variant="outline" size="sm" onClick={() => setShowMentions((s) => !s)} className="text-xs"><AtSign className="h-3.5 w-3.5 mr-1" />{zh ? "提醒" : "Mention"}{mentions.length > 0 && ` (${mentions.length})`}</Button>
            {showMentions && (
              <div className="absolute z-30 mt-1 w-60 max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg p-1" data-testid="note-mention-list">
                {groups.length > 0 && (
                  <div className="px-1 py-1 mb-1 border-b border-slate-100 flex flex-wrap gap-1">
                    {groups.map((g) => (
                      <button key={g.key} type="button" data-testid={`note-mention-group-${g.key}`} onClick={() => addGroup(g.members)}
                        className="text-[11px] bg-slate-100 hover:bg-brand-50 hover:text-brand-500 rounded-full px-2 py-0.5">+ {g.label} ({g.members.length})</button>
                    ))}
                  </div>
                )}
                {users.map((u) => {
                  const on = mentions.includes(u.username);
                  return (
                    <button key={u.username} type="button" data-testid={`note-mention-${u.username}`}
                      onClick={() => setMentions(on ? mentions.filter((m) => m !== u.username) : [...mentions, u.username])}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left ${on ? "bg-red-50 text-red-700" : "hover:bg-slate-50"}`}>
                      <Avatar src={u.avatar} name={nameOf(u)} size={22} />
                      <span className="flex-1 truncate">{nameOf(u)}</span>
                      <span className="text-[10px] text-slate-400 capitalize">{u.role}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <Button type="button" data-testid="note-important-toggle" variant="outline" size="sm" onClick={() => setImportant((v) => !v)}
            className={`text-xs ${important ? "bg-red-600 text-white border-red-600 hover:bg-red-700 hover:text-white" : "text-red-600 border-red-200 hover:bg-red-50"}`}
            title={zh ? "标为重要更新会站内+短信提醒负责销售" : "Notify the salesperson (in-app + SMS)"}>
            <AlertTriangle className="h-3.5 w-3.5 mr-1" />{zh ? "标为重要" : "Important"}
          </Button>
          <Button type="button" data-testid="note-submit-btn" size="sm" onClick={submit} disabled={posting || !!uploading} className="ml-auto bg-brand-500 hover:bg-brand-600 text-white">
            <Send className="h-3.5 w-3.5 mr-1" />{posting ? (zh ? "发布中…" : "Posting…") : (zh ? "发布" : "Post")}
          </Button>
        </div>
      </div>

      {/* Filter toolbar + photo wall toggle */}
      <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="daily-logs-filters">
        {[["all", zh ? "全部" : "All"], ["media", zh ? "仅图片/视频" : "Media"], ["mine", zh ? "仅 @ 我" : "@ Me"]].map(([k, lbl]) => (
          <button key={k} data-testid={`log-filter-${k}`} onClick={() => setFilter(k)}
            className={`text-xs px-2.5 py-1 rounded-full border ${filter === k ? "bg-brand-500 text-white border-brand-500" : "bg-white text-slate-500 border-slate-200 hover:border-brand-300"}`}>{lbl}</button>
        ))}
        <button data-testid="log-photowall-toggle" onClick={() => setWall((w) => !w)}
          className={`text-xs px-2.5 py-1 rounded-full border ml-auto ${wall ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}>
          <ImageIcon className="h-3.5 w-3.5 inline mr-1" />{zh ? `图片墙 (${mediaItems.length})` : `Photo wall (${mediaItems.length})`}
        </button>
      </div>

      <div className="mt-2">
        <ProjectKeywordInput value={keyword} onChange={setKeyword} zh={zh} testId="daily-logs-keyword"
          label={zh ? "搜索日志内容、发布人或附件" : "Search logs, authors or attachments"} />
      </div>
      {search.active && search.loading && <p role="status" className="mt-2 text-xs text-slate-500">{zh ? "搜索中…" : "Searching…"}</p>}
      {search.active && search.error && <p role="alert" className="mt-2 text-xs text-red-600">
        {zh ? "搜索失败，请重试" : "Search failed. Please retry."}{" "}
        <button type="button" onClick={search.refresh} className="underline focus-visible:ring-2 focus-visible:ring-brand-400">{zh ? "重试" : "Retry"}</button>
      </p>}

      {/* Photo wall */}
      {wall ? (
        <div className="mt-3" data-testid="photo-wall">
          <div className="flex gap-1.5 mb-2">
            {[["stage", zh ? "按阶段" : "By stage"], ["date", zh ? "按日期" : "By date"]].map(([k, l]) => (
              <button key={k} data-testid={`wall-group-${k}`} onClick={() => setWallGroup(k)}
                className={`text-xs px-2.5 py-0.5 rounded-full border ${wallGroup === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}>{l}</button>
            ))}
          </div>
          {mediaItems.length === 0 && (!search.active || (!search.loading && !search.error)) && <p className="text-center text-slate-300 text-sm py-4">{search.active ? (zh ? "无匹配图片/视频" : "No matching media") : (zh ? "暂无图片/视频" : "No media")}</p>}
          {wallGroups.map(([label, items]) => (
            <div key={label} className="mb-4" data-testid={`wall-group-section-${label}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs font-semibold text-red-600">{label}</span>
                <span className="text-[10px] text-slate-400">· {items.length}</span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {items.map((a) => {
                  const previewIndex = wallItems.findIndex((item) => item.fileId === a.fileId);
                  return (
                  <button key={a.fileId} type="button" onClick={() => setWallPreview(previewIndex)}
                    aria-label={`${zh ? "查看" : "View"} ${a.filename || a.fileId}`}
                    data-testid={`wall-item-${a.fileId}`}
                    className="relative block w-full p-0 text-left rounded-md overflow-hidden border border-slate-200 aspect-square bg-slate-100">
                    {a.kind === "image"
                      ? <img src={fileUrl(a.fileId)} alt={a.filename} className="w-full h-full object-cover" />
                      : <span className="w-full h-full flex items-center justify-center bg-black"><Film className="h-6 w-6 text-white/80" /></span>}
                    {/* 角标同样展示归并后的阶段名，与分组标题一致（原始 phaseAtPost 不回写） */}
                    <span className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-[9px] px-1 py-0.5 truncate">{displayPhase(a.note.phaseAtPost, zh) || (zh ? "未标注" : "—")} · {String(a.note.createdAt).slice(5, 10)}</span>
                  </button>
                );})}
              </div>
            </div>
          ))}
        </div>
      ) : (
      /* Feed */
      <div className="mt-3 space-y-3">
        {/* 三态空区：加载中 / 加载失败（可重试） / 真的没有日志。
            旧实现把后两者都显示成「暂无记录」，会把网络失败误导成「日志被清空」。 */}
        {!search.active && loading && notes.length === 0 && (
          <p className="flex items-center justify-center gap-2 text-center text-slate-400 text-sm py-4" data-testid="daily-logs-loading">
            <Loader2 className="h-4 w-4 animate-spin" />{zh ? "加载日志中…" : "Loading logs…"}
          </p>
        )}
        {!search.active && !loading && loadError && notes.length === 0 && (
          <div className="text-center py-4" data-testid="daily-logs-error">
            <p className="text-sm text-red-600 inline-flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" />{zh ? "日志加载失败，请重试" : "Failed to load logs"}</p>
            <p className="mt-1 text-xs text-slate-400 break-words">{String(loadError).slice(0, 160)}</p>
            <Button type="button" variant="outline" size="sm" onClick={load} className="mt-2 text-xs" data-testid="daily-logs-retry">
              <RefreshCw className="h-3.5 w-3.5 mr-1" />{zh ? "重新加载" : "Retry"}
            </Button>
          </div>
        )}
        {(search.active ? !search.loading && !search.error : !loading && !loadError) && filteredNotes.length === 0 && <p className="text-center text-slate-300 text-sm py-4">{!search.active && notes.length === 0 ? (zh ? "暂无记录，发布第一条项目日志" : "No logs yet") : (zh ? "无匹配记录" : "No matching notes")}</p>}
        {/* 已有数据但本次刷新失败：保留列表，顶部提示一次，不打断阅读 */}
        {!search.active && !loading && loadError && notes.length > 0 && (
          <p className="text-xs text-amber-600 inline-flex items-center gap-1.5" data-testid="daily-logs-stale">
            <AlertTriangle className="h-3.5 w-3.5" />{zh ? "刷新失败，显示的是上次加载的日志" : "Refresh failed; showing previously loaded logs"}
            <button type="button" onClick={load} className="underline hover:text-amber-700">{zh ? "重试" : "Retry"}</button>
          </p>
        )}
        {filteredNotes.map((n) => (
          <div key={n.id} className={`rounded-lg border bg-white p-3 transition-all ${highlight === n.id ? "border-red-400 ring-2 ring-red-300" : n.important ? "border-red-200 bg-red-50/30" : "border-slate-100"}`} data-testid={`note-item-${n.id}`}>
            <div className="flex items-start gap-2.5">
              <Avatar src={avatarFor(n.authorName)} name={zh ? (n.authorNameZh || n.authorName) : n.authorName} size={32} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-800">{zh ? (n.authorNameZh || n.authorName) : (n.authorName || n.authorNameZh)}</span>
                  <span className="text-[10px] text-slate-400 capitalize">{n.role}</span>
                  <span className="text-[11px] text-slate-400">{fmtDT(n.createdAt)}</span>
                  {n.important && (
                    <span data-testid={`note-important-${n.id}`} className="text-[10px] font-bold text-white bg-red-600 rounded-full px-1.5 py-0.5 inline-flex items-center gap-0.5">
                      <AlertTriangle className="h-3 w-3" />{zh ? "重要" : "IMPORTANT"}
                    </span>
                  )}
                  {(n.authorUsername === user.username || ["admin", "ceo"].includes(user.role)) && (
                    <button data-testid={`note-del-${n.id}`} onClick={() => del(n)} className="ml-auto text-slate-300 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </div>
                {n.text && <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap break-words">{n.text}</p>}

                {(n.mentions || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {n.mentions.map((m) => {
                      const u = (directory || []).find((x) => x.username === m);
                      const read = (n.readBy || []).includes(m);
                      return (
                        <span key={m} data-testid={`mention-status-${n.id}-${m}`}
                          className={`text-[11px] rounded-full px-1.5 py-0.5 flex items-center gap-0.5 ${read ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                          @{u ? nameOf(u) : m}{read ? <Check className="h-3 w-3" /> : <span className="text-[10px]">· {zh ? "待查看" : "unseen"}</span>}
                        </span>
                      );
                    })}
                  </div>
                )}

                {(n.attachments || []).length > 0 && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {n.attachments.map((a) => (
                      <div key={a.fileId} data-testid={`note-attachment-${a.fileId}`}>
                        {a.kind === "image" ? (
                          <a href={fileUrl(a.fileId)} target="_blank" rel="noreferrer"><img src={fileUrl(a.fileId)} alt={a.filename} className="rounded-md border border-slate-200 max-h-44 w-full object-cover" /></a>
                        ) : a.kind === "video" ? (
                          <video controls src={fileUrl(a.fileId)} className="rounded-md border border-slate-200 max-h-48 w-full bg-black" />
                        ) : (
                          <a href={fileUrl(a.fileId)} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-md border border-slate-200 px-2.5 py-2 hover:border-brand-300 hover:bg-brand-50/40">
                            <AttachIcon kind={a.kind} /><span className="text-xs text-slate-700 truncate flex-1">{a.filename}</span><ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {(n.links || []).length > 0 && (
                  <div className="flex flex-col gap-1 mt-2">
                    {/* 兼容历史数据里 {url,title} 对象与字符串两种形态，见 normalizeLinks 注释 */}
                    {normalizeLinks(n.links).map((l, i) => (
                      <a key={i} href={l.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-sky-600 hover:underline truncate">
                        <Link2 className="h-3.5 w-3.5 flex-shrink-0" />{l.title || l.url}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {search.active && search.hasMore && <button type="button" onClick={search.loadMore} disabled={search.loadingMore}
        data-testid="daily-logs-search-more" className="mt-3 w-full rounded-md border border-slate-200 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-brand-400 disabled:opacity-50">
        {search.loadingMore ? (zh ? "加载中…" : "Loading…") : (zh ? "加载更多匹配日志" : "Load more matching logs")}
      </button>}

      {/* Export dialog */}
      <Dialog open={showExport} onOpenChange={setShowExport}>
        <DialogContent className="max-w-md" data-testid="export-dialog">
          <DialogHeader><DialogTitle>{zh ? "导出 / 邮寄进度报告" : "Export / Email Report"}</DialogTitle>
            <DialogDescription>{zh ? "可选时间段与仅含图片，导出 PDF 或一键邮寄业主" : "Optional date range & images-only; export PDF or email the owner"}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-xs text-slate-500">{zh ? "起始日期" : "Start"}</span>
                <input type="date" data-testid="export-start" className="mt-1 w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" value={exp.start} onChange={(e) => setExp({ ...exp, start: e.target.value })} /></label>
              <label className="block"><span className="text-xs text-slate-500">{zh ? "结束日期" : "End"}</span>
                <input type="date" data-testid="export-end" className="mt-1 w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" value={exp.end} onChange={(e) => setExp({ ...exp, end: e.target.value })} /></label>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" data-testid="export-images-only" checked={exp.imagesOnly} onChange={(e) => setExp({ ...exp, imagesOnly: e.target.checked })} />
              {zh ? "仅含图片（现场照片存档）" : "Images only (photo archive)"}
            </label>
            <label className="block"><span className="text-xs text-slate-500">{zh ? "业主邮箱（留空则用项目预留邮箱）" : "Owner email (blank = project email)"}</span>
              <input type="email" data-testid="export-owner-email" className="mt-1 w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" placeholder="owner@example.com" value={exp.to} onChange={(e) => setExp({ ...exp, to: e.target.value })} /></label>
            <div className="flex gap-2 pt-1">
              <Button data-testid="export-download-btn" variant="outline" className="flex-1" onClick={exportPdf}><Download className="h-4 w-4 mr-1" />{zh ? "下载 PDF" : "Download"}</Button>
              <Button data-testid="export-email-btn" className="flex-1 bg-brand-500 hover:bg-brand-600 text-white" onClick={emailOwner} disabled={busy}><Mail className="h-4 w-4 mr-1" />{busy ? (zh ? "发送中…" : "Sending…") : (zh ? "邮寄业主" : "Email owner")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share link dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md" data-testid="share-dialog">
          <DialogHeader><DialogTitle>{zh ? "业主查看链接" : "Owner share link"}</DialogTitle>
            <DialogDescription>{zh ? "只读图片墙+进度看板链接，无需登录，可设置有效期或一键失效" : "Read-only progress + photo-wall link, no login, with expiry & revoke"}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <label className="block"><span className="text-xs text-slate-500">{zh ? "有效期" : "Validity"}</span>
              <select data-testid="share-days" className="mt-1 w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" value={shareDays} onChange={(e) => setShareDays(e.target.value)}>
                <option value="7">{zh ? "7 天" : "7 days"}</option>
                <option value="30">{zh ? "30 天" : "30 days"}</option>
                <option value="90">{zh ? "90 天" : "90 days"}</option>
                <option value="0">{zh ? "永久有效" : "Never expires"}</option>
              </select></label>

            {shareLink && shareLink.token && !shareLink.expired ? (
              <>
                <div className="flex items-center gap-2">
                  <input data-testid="share-url" readOnly value={shareUrl} className="flex-1 text-sm rounded-md border border-slate-200 px-2.5 py-2 bg-slate-50" />
                  <Button data-testid="share-copy-btn" variant="outline" size="sm" onClick={async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success(zh ? "已复制" : "Copied"); } catch {} }}><Copy className="h-4 w-4" /></Button>
                </div>
                <p className="text-[11px] text-slate-400">{shareLink.expiresAt ? (zh ? "到期：" : "Expires: ") + fmtD(shareLink.expiresAt) : (zh ? "永久有效" : "Never expires")}</p>
                <div className="flex gap-2">
                  <a href={shareUrl} target="_blank" rel="noreferrer" className="text-xs text-red-600 hover:underline flex items-center gap-1"><ExternalLink className="h-3.5 w-3.5" />{zh ? "预览" : "Preview"}</a>
                  <Button data-testid="share-rotate-btn" variant="outline" size="sm" className="ml-auto text-xs" onClick={() => genShare(true)}>{zh ? "重置链接" : "Rotate"}</Button>
                  <Button data-testid="share-revoke-btn" variant="outline" size="sm" className="text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={revokeShare}>{zh ? "一键失效" : "Revoke"}</Button>
                </div>
              </>
            ) : (
              <Button data-testid="share-generate-btn" onClick={() => genShare(false)} className="w-full bg-brand-500 hover:bg-brand-600 text-white"><Share2 className="h-4 w-4 mr-1" />{zh ? "生成分享链接" : "Generate link"}</Button>
            )}
            <OwnerLinkCheck api={api} projectId={projectId} zh={zh} onFixed={openShare} />
          </div>
        </DialogContent>
      </Dialog>

      {wallPreview !== null && wallLightboxItems[wallPreview] && (
        <MediaLightbox tid="daily-log-photo-wall-lightbox" items={wallLightboxItems} index={wallPreview}
          onIndex={setWallPreview} onClose={() => setWallPreview(null)} api={api} zh={zh} />
      )}
    </div>
  );
}

function AttachIcon({ kind }) {
  if (kind === "pdf") return <FileText className="h-4 w-4 text-red-500" />;
  if (kind === "video") return <Film className="h-4 w-4 text-indigo-500" />;
  return <Paperclip className="h-4 w-4 text-slate-500" />;
}
